import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { formatCurrency, formatExpirationDate } from '@monorepo/client-common';
import {
  FORBIDDEN_PRACTICES_LINKS,
  getTradingSystemName,
} from '@monorepo/client-types';
import { Icons } from '@monorepo/icons';
import { Button, Flex, TextComponent } from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { Account, Application, IconsNames } from '@monorepo/types';
import { AccountInfoCompact } from './account-info-compact';

import './account-info.scss';

const project = EnvService.application;

interface AccountInfoProps {
  account: Account;
}

const AccountInfo: FunctionComponent<AccountInfoProps> = ({ account }) => {
  const { isDesktop } = useResponsiveness();
  const {
    dataStore: { programStore },
  } = useStore();
  const { t } = useTranslation(['dashboardTemplates']);
  const program = programStore.get(account.programId);

  const expiredDate = formatExpirationDate(account.expireDate);

  const data = [
    {
      title: t('dashboardTemplates:account_type'),
      value: account.type,
      bold: true,
      titleIcon: IconsNames.CloudAuditing,
    },
    {
      title: t('dashboardTemplates:account_platform'),
      value: t(`programs:trading_system.${account.tradingSystem}_hub`, {
        defaultValue: getTradingSystemName(account.tradingSystem) || '-',
      }),
      titleIcon: IconsNames.Rocket,
    },
    {
      title:
        project === Application.TTP
          ? t('dashboardTemplates:account_buying_power')
          : t('dashboardTemplates:account_size'),
      value: formatCurrency(program?.config.buyingPower || 0, {
        decimalPlaces: 2,
        currency: 'USD',
      }),
      titleIcon: IconsNames.Bank,
    },
    {
      title: t('dashboardTemplates:status'),
      value: (
        <TextComponent capitalize>
          {account.status.replace(/-/g, ' ')}
        </TextComponent>
      ),
      titleIcon: IconsNames.Recycle,
    },
    {
      title: t('dashboardTemplates:program_ends'),
      value: expiredDate,
      titleIcon: IconsNames.ClockOutline,
    },
  ];

  const AccountInfoItems = () => {
    return (
      <Flex vertical gap={16} className="account-info-items">
        {data.map((item) => (
          <Flex gap={5} justify="space-between" key={item.title}>
            <Flex align="center" gap={8}>
              <TextComponent level={6} responsive={false}>
                <Icons iconName={item.titleIcon} />
              </TextComponent>
              <TextComponent level={8} responsive={false}>
                {item.title}
              </TextComponent>
            </Flex>
            <TextComponent
              textAlign="right"
              capitalize
              weight={item.bold ? 700 : 500}
              level={6}
            >
              {item.value}
            </TextComponent>
          </Flex>
        ))}
        <Button
          href={FORBIDDEN_PRACTICES_LINKS[project]}
          target="_blank"
          icon={
            <Icons
              iconName={IconsNames.Bank}
              className="forbidden-practices-icon"
            />
          }
        >
          {t('dashboardTemplates:forbidden_practices')}
        </Button>
      </Flex>
    );
  };

  if (isDesktop) {
    return <AccountInfoItems />;
  }

  return (
    <AccountInfoCompact>
      <AccountInfoItems />
    </AccountInfoCompact>
  );
};

export default AccountInfo;
